import { PhotoFolderToolbar, type FolderSelection } from '../photos/PhotoFolderToolbar';
import { Checkbox } from '@mui/joy';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Modal,
  CircularProgress,
  Chip,
  Tooltip,
  FormControl,
  FormLabel,
  Input,
  Button,
} from '@mui/joy';
import { FileText, DownloadSimple as Download, Trash as Trash2, PencilSimple as Edit2, Tag, Calendar, File, X } from '@phosphor-icons/react';
import '../../lib/pdfWorkerSetup'; // Initialize PDF worker
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import type { Document as DocType } from '../../types';
import { useUpdateDoc, useDeleteDoc } from '../../hooks/useOpportunity';

interface DocumentGalleryProps {
  documents: DocType[];
  folderParent?: { job?: number; opportunity?: number };
  loading?: boolean;
  opportunityId?: number;
  onRefresh?: () => void;
  onDocumentUpdate?: () => void;
  onDocumentDelete?: () => void;
}

export const DocumentGallery: React.FC<DocumentGalleryProps> = ({
  documents,
  folderParent,
  loading = false,
  opportunityId,
  onRefresh,
  onDocumentUpdate,
  onDocumentDelete
}) => {
  const [folder, setFolder] = useState<FolderSelection>('all');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const visibleDocuments = documents.filter(doc => folder === 'all' || (folder === 'unfiled' ? !doc.folder : doc.folder === folder));
  const folderToolbar = folderParent && <PhotoFolderToolbar kind="documents" parent={folderParent} active={folder} onSelect={value => { setFolder(value); setSelected(new Set()); }} selectedIds={selected} photos={documents} onChanged={() => { setSelected(new Set()); onDocumentUpdate?.(); }} />;
  const [hoveredDocId, setHoveredDocId] = useState<number | null>(null);
  const [editingDoc, setEditingDoc] = useState<DocType | null>(null);
  const [editFormData, setEditFormData] = useState<{ tagNames: string }>({ 
    tagNames: '' 
  });
  const [selectedDoc, setSelectedDoc] = useState<DocType | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  
  const updateDocMutation = useUpdateDoc();
  const deleteDocMutation = useDeleteDoc();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFileIcon = (doc: DocType) => {
    if (doc.is_pdf) return <FileText size={20} />;
    if (doc.is_image) return <File size={20} />;
    return <File size={20} />;
  };

  const handleDownload = async (doc: DocType, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    
    setIsDownloading(true);
    try {
      // Fetch the file as a blob to force download
      const response = await fetch(doc.file_url);
      const blob = await response.blob();
      
      // Create object URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_filename || `document-${doc.id}`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to direct link
      const link = document.createElement('a');
      link.href = doc.file_url;
      link.download = doc.original_filename || `document-${doc.id}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDeleteDocument = async (docId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await deleteDocMutation.mutateAsync({ docId, opportunityId });
      if (onDocumentDelete) {
        onDocumentDelete();
      }
    } catch (error) {
      alert('Failed to delete document. Please try again.');
    }
  };

  const handleEditDocument = (doc: DocType, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingDoc(doc);
    setEditFormData({
      tagNames: (doc.tags || []).map(tag => tag.name).join(', ')
    });
  };

  const handleCardClick = (doc: DocType) => {
    setSelectedDoc(doc);
    setPageNumber(1);
    setPdfLoadError(false);
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfLoadError(false);
  };

  const onDocumentLoadError = () => {
    setPdfLoadError(true);
  };

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(numPages || 1, prev + 1));
  };

  // Handle keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedDoc && e.key === 'Escape') {
        setSelectedDoc(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedDoc]);

  const handleSaveDocumentEdit = async () => {
    if (!editingDoc) return;

    try {
      await updateDocMutation.mutateAsync({
        docId: editingDoc.id,
        tagNames: editFormData.tagNames
      });
      
      setEditingDoc(null);
      if (onDocumentUpdate) {
        onDocumentUpdate();
      }
    } catch (error) {
      alert('Failed to update document. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Ensure documents is always an array
  const safeDocuments = Array.isArray(documents) ? documents : [];
  
  if (safeDocuments.length === 0 && !folderParent) {
    return (
      <Typography level="body-sm" textColor="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No documents uploaded yet.
      </Typography>
    );
  }

  return (
    <>
      {folderToolbar}
      {folderParent && visibleDocuments.length === 0 && <Typography level="body-sm" sx={{ p: 2 }}>No documents in this folder.</Typography>}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {visibleDocuments.map((doc) => (
          <Card 
            key={doc.id} 
            variant="soft"
            onMouseEnter={() => setHoveredDocId(doc.id)}
            onMouseLeave={() => setHoveredDocId(null)}
            onClick={() => handleCardClick(doc)}
            sx={{ 
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 'sm'
              }
            }}
          >
            {folderParent && <Checkbox checked={selected.has(doc.id)} label="Select" onClick={event => event.stopPropagation()} onChange={event => { const checked = event.target.checked; setSelected(previous => { const next = new Set(previous); if (checked) next.add(doc.id); else next.delete(doc.id); return next; }); }} />}
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {getFileIcon(doc)}
                  <Box sx={{ flex: 1 }}>
                    <Typography level="body-sm" fontWeight="lg">
                      {doc.original_filename || 'Document'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography level="body-xs" textColor="text.secondary">
                        {doc.file_size_display}
                      </Typography>
                      <Typography level="body-xs" textColor="text.secondary">•</Typography>
                      <Typography level="body-xs" textColor="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Calendar size={12} />
                        {formatDate(doc.uploaded_at)}
                      </Typography>
                    </Box>
                    {Array.isArray(doc.tags) && doc.tags.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        {doc.tags.map((tag) => (
                          <Chip 
                            key={tag.id} 
                            size="sm" 
                            variant="soft" 
                            startDecorator={<Tag size={10} />}
                            sx={{ fontSize: '11px' }}
                          >
                            {tag.name}
                          </Chip>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {hoveredDocId === doc.id && (
                    <>
                      <Tooltip title="Edit tags">
                        <IconButton
                          size="sm"
                          variant="solid"
                          color="primary"
                          onClick={(e) => handleEditDocument(doc, e)}
                        >
                          <Edit2 size={16} />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Delete document">
                        <IconButton
                          size="sm"
                          variant="solid"
                          color="danger"
                          onClick={(e) => handleDeleteDocument(doc.id, e)}
                          disabled={deleteDocMutation.isPending}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  
                  <Tooltip title="Download">
                    <IconButton
                      size="sm"
                      variant="outlined"
                      color="primary"
                      onClick={(e) => handleDownload(doc, e)}
                      disabled={isDownloading}
                    >
                      <Download size={16} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Edit Document Modal */}
      {editingDoc && (
        <Modal
          open={!!editingDoc}
          onClose={() => setEditingDoc(null)}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 500,
              maxWidth: '90vw',
              bgcolor: 'background.surface',
              borderRadius: 'lg',
              boxShadow: 'lg',
              p: 3,
            }}
          >
            <Typography level="h4" sx={{ mb: 3 }}>
              Edit Document Tags
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography level="body-sm" fontWeight="lg">
                {editingDoc.original_filename}
              </Typography>
              <Typography level="body-xs" textColor="text.secondary">
                {editingDoc.file_size_display}
              </Typography>
            </Box>

            <FormControl sx={{ mb: 3 }}>
              <FormLabel>Tags (comma-separated)</FormLabel>
              <Input
                value={editFormData.tagNames}
                onChange={(e) => setEditFormData({ ...editFormData, tagNames: e.target.value })}
                placeholder="e.g., contract, signed, final"
              />
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setEditingDoc(null)}
                disabled={updateDocMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="primary"
                onClick={handleSaveDocumentEdit}
                loading={updateDocMutation.isPending}
              >
                Save Changes
              </Button>
            </Box>
          </Box>
        </Modal>
      )}

      {/* Document Lightbox Modal */}
      {selectedDoc && (
        <Modal
          open={!!selectedDoc}
          onClose={() => setSelectedDoc(null)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1300,
            }}
            onClick={() => setSelectedDoc(null)}
          >
            {/* Close button */}
            <IconButton
              variant="plain"
              color="neutral"
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 1,
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDoc(null);
              }}
            >
              <X size={24} />
            </IconButton>

            {/* Document viewer container */}
            <Box
              sx={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Document info */}
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Typography level="title-md" sx={{ color: 'white', mb: 1 }}>
                  {selectedDoc.original_filename}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'white', opacity: 0.8 }}>
                  {selectedDoc.file_size_display} • Uploaded {formatDate(selectedDoc.uploaded_at)}
                </Typography>
              </Box>

              {/* Document preview */}
              <Box 
                sx={{ 
                  width: '100%',
                  maxWidth: '1200px',
                  height: '70vh',
                  bgcolor: 'white',
                  borderRadius: 'md',
                  overflow: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {selectedDoc.is_pdf ? (
                  pdfLoadError ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <FileText size={64} />
                      <Typography level="title-lg" sx={{ mt: 2, mb: 3 }}>
                        Unable to load PDF preview
                      </Typography>
                      <Typography level="body-md" sx={{ mb: 3 }}>
                        The PDF cannot be displayed in the browser.
                      </Typography>
                      <Button
                        variant="solid"
                        color="primary"
                        startDecorator={<Download />}
                        onClick={() => handleDownload(selectedDoc)}
                        loading={isDownloading}
                      >
                        Download PDF
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ 
                      width: '100%', 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      position: 'relative',
                      bgcolor: '#525659',
                      overflow: 'auto',
                      p: 2
                    }}>
                      <Document
                        file={selectedDoc.file_url}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        loading={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <CircularProgress />
                          </Box>
                        }
                      >
                        {numPages && Array.from(new Array(numPages), (el, index) => (
                          <Box key={`page_${index + 1}`} sx={{ mb: 2 }}>
                            {/* Page number label */}
                            <Typography 
                              level="body-sm" 
                              sx={{ 
                                textAlign: 'center', 
                                color: 'white', 
                                mb: 1,
                                opacity: 0.8 
                              }}
                            >
                              Page {index + 1} of {numPages}
                            </Typography>
                            <Page 
                              pageNumber={index + 1}
                              renderTextLayer={true}
                              renderAnnotationLayer={true}
                              width={Math.min(1200, window.innerWidth * 0.9)}
                              loading={
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center', 
                                  height: 200,
                                  color: 'white'
                                }}>
                                  <CircularProgress size="sm" />
                                </Box>
                              }
                            />
                          </Box>
                        ))}
                      </Document>
                    </Box>
                  )
                ) : selectedDoc.is_image ? (
                  <img
                    src={selectedDoc.file_url}
                    alt={selectedDoc.original_filename}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <FileText size={64} />
                    <Typography level="title-lg" sx={{ mt: 2, mb: 3 }}>
                      Preview not available
                    </Typography>
                    <Typography level="body-md" sx={{ mb: 3 }}>
                      This file type cannot be previewed in the browser.
                    </Typography>
                    <Button
                      variant="solid"
                      color="primary"
                      startDecorator={<Download />}
                      onClick={() => handleDownload(selectedDoc)}
                      loading={isDownloading}
                    >
                      Download File
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Download button for previewable files */}
              {(selectedDoc.is_pdf || selectedDoc.is_image) && (
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="solid"
                    color="primary"
                    startDecorator={<Download />}
                    onClick={() => handleDownload(selectedDoc)}
                    loading={isDownloading}
                  >
                    Download {selectedDoc.is_pdf ? 'PDF' : 'Image'}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};